// Cambios locales de stock: movimientos, compras, anulaciones y tomas de inventario.
// Funciones puras (testeables sin navegador). Las reglas de signo y motivo son las de core,
// que el servidor vuelve a validar.
import { MOVEMENT_TYPES, REASON_REQUIRED, isValidMovementQty, type MovementType } from '@pos/core';
import type { LocalChange, PurchaseItemRow, PurchaseRow } from '@pos/local-db';
import { costChanges } from '../products/changes.ts';

export interface StockContext {
  businessId: string;
  deviceId: string | null;
  /** Autor (para mostrarlo antes de sincronizar; el servidor pone el suyo igual). */
  userId?: string | null;
  now: string;
  newId: () => string;
}

export class StockError extends Error {}

export interface MovementInput {
  variantId: string;
  type: MovementType;
  /** Cantidad con signo, en milésimas. */
  qtyMilli: number;
  reason?: string | null;
  refType?: 'purchase' | 'sale' | 'count' | null;
  refId?: string | null;
  occurredAt?: string;
}

export function buildMovement(input: MovementInput, ctx: StockContext): LocalChange {
  if (!isValidMovementQty(input.type, input.qtyMilli)) {
    throw new StockError(
      input.qtyMilli === 0 ? 'La cantidad no puede ser cero.' : `Cantidad inválida para "${MOVEMENT_TYPES[input.type].label}".`,
    );
  }
  const reason = input.reason?.trim() || null;
  if (REASON_REQUIRED.includes(input.type) && (!reason || reason.length < 2)) {
    throw new StockError('Escribí el motivo (queda registrado).');
  }
  return {
    table: 'stock_movements',
    kind: 'insert',
    row: {
      id: ctx.newId(),
      business_id: ctx.businessId,
      variant_id: input.variantId,
      type: input.type,
      qty_milli: input.qtyMilli,
      reason,
      ref_type: input.refType ?? null,
      ref_id: input.refId ?? null,
      occurred_at: input.occurredAt ?? ctx.now,
      device_id: ctx.deviceId,
      created_by: ctx.userId ?? null,
      created_at: ctx.now,
    },
  };
}

// ---------------------------------------------------------------------------
// Compras
// ---------------------------------------------------------------------------

export interface PurchaseLine {
  variantId: string;
  qtyMilli: number;
  /** Costo unitario en centavos (solo el dueño). */
  unitCost: number | null;
}

export interface PurchaseInput {
  supplierId: string | null;
  invoiceNumber: string | null;
  occurredAt: string;
  notes: string | null;
  lines: PurchaseLine[];
  /** El dueño carga costos; un empleado solo cantidades. */
  withCosts: boolean;
  /** Si es true y hay costo, actualiza el costo del producto (último costo). */
  updateCosts: boolean;
  /** Costos conocidos en este dispositivo, por variante. */
  knownCosts: Map<string, number>;
}

/** Costo de una línea: unitario × cantidad (en milésimas), redondeado al centavo. */
export function lineCost(unitCost: number, qtyMilli: number): number {
  return Math.round((unitCost * qtyMilli) / 1000);
}

export function buildPurchaseChanges(input: PurchaseInput, ctx: StockContext): { purchaseId: string; changes: LocalChange[] } {
  const lines = input.lines.filter((l) => l.qtyMilli !== 0);
  if (lines.length === 0) throw new StockError('Agregá al menos un producto con cantidad.');
  for (const l of lines) {
    if (!Number.isSafeInteger(l.qtyMilli) || l.qtyMilli < 0) throw new StockError('Hay cantidades inválidas.');
    if (l.unitCost !== null && (!Number.isSafeInteger(l.unitCost) || l.unitCost < 0)) throw new StockError('Hay costos inválidos.');
  }
  const costs = input.withCosts ? lines.map((l) => l.unitCost) : lines.map(() => null);
  const total = input.withCosts && costs.every((c) => c !== null) ? lines.reduce((sum, l, i) => sum + lineCost(costs[i]!, l.qtyMilli), 0) : null;

  const purchaseId = ctx.newId();
  const changes: LocalChange[] = [
    {
      table: 'purchases',
      kind: 'insert',
      row: {
        id: purchaseId,
        business_id: ctx.businessId,
        supplier_id: input.supplierId,
        invoice_number: input.invoiceNumber?.trim() || null,
        occurred_at: input.occurredAt,
        notes: input.notes?.trim() || null,
        total,
        voided_at: null,
        void_reason: null,
        device_id: ctx.deviceId,
        created_by: ctx.userId ?? null,
        created_at: ctx.now,
        updated_at: ctx.now,
      },
    },
  ];
  lines.forEach((l, i) => {
    changes.push({
      table: 'purchase_items',
      kind: 'insert',
      row: {
        id: ctx.newId(),
        business_id: ctx.businessId,
        purchase_id: purchaseId,
        variant_id: l.variantId,
        qty_milli: l.qtyMilli,
        unit_cost: costs[i] ?? null,
        created_at: ctx.now,
        updated_at: ctx.now,
      },
    });
    changes.push(
      buildMovement({ variantId: l.variantId, type: 'purchase', qtyMilli: l.qtyMilli, refType: 'purchase', refId: purchaseId, occurredAt: input.occurredAt }, ctx),
    );
  });

  // Último costo: el de esta compra pasa a ser el costo del producto.
  if (input.withCosts && input.updateCosts) {
    const seen = new Set<string>();
    for (const [i, l] of lines.entries()) {
      const cost = costs[i];
      if (cost === null || cost === undefined || seen.has(l.variantId)) continue;
      seen.add(l.variantId);
      changes.push(...costChanges(l.variantId, ctx.businessId, cost, input.knownCosts.get(l.variantId), ctx.now));
    }
  }
  return { purchaseId, changes };
}

/** Anular una compra: queda marcada y se descuenta lo que había ingresado. */
export function buildPurchaseVoidChanges(
  purchase: PurchaseRow,
  items: PurchaseItemRow[],
  reason: string,
  ctx: StockContext,
): LocalChange[] {
  if (purchase.voided_at) throw new StockError('La compra ya está anulada.');
  if (reason.trim().length < 2) throw new StockError('Escribí el motivo de la anulación.');
  return [
    { table: 'purchases', kind: 'patch', id: purchase.id, changes: { voided_at: ctx.now, void_reason: reason.trim() } },
    ...items.map((item) =>
      buildMovement({ variantId: item.variant_id, type: 'purchase_void', qtyMilli: -item.qty_milli, refType: 'purchase', refId: purchase.id }, ctx),
    ),
  ];
}

/**
 * Completar o corregir los costos de una compra (típicamente la de un empleado, que carga solo
 * cantidades). Solo cambia costos y total: el stock ya se movió al cargarla.
 */
export function buildPurchaseCostChanges(
  purchase: PurchaseRow,
  items: PurchaseItemRow[],
  costs: Map<string, number | null>,
  options: { updateCosts: boolean; knownCosts: Map<string, number> },
  ctx: StockContext,
): LocalChange[] {
  if (purchase.voided_at) throw new StockError('La compra está anulada: no se pueden cambiar sus costos.');
  const unit = items.map((item) => costs.get(item.id) ?? null);
  if (unit.some((c) => c === null)) throw new StockError('Completá el costo de todos los productos.');
  if (unit.some((c) => !Number.isSafeInteger(c) || c! < 0)) throw new StockError('Hay costos inválidos.');

  const changes: LocalChange[] = [];
  items.forEach((item, i) => {
    if (item.unit_cost !== unit[i]) changes.push({ table: 'purchase_items', kind: 'patch', id: item.id, changes: { unit_cost: unit[i] } });
  });
  const total = items.reduce((sum, item, i) => sum + lineCost(unit[i]!, item.qty_milli), 0);
  if (purchase.total !== total) changes.push({ table: 'purchases', kind: 'patch', id: purchase.id, changes: { total } });

  if (options.updateCosts) {
    const seen = new Set<string>();
    items.forEach((item, i) => {
      if (seen.has(item.variant_id)) return;
      seen.add(item.variant_id);
      changes.push(...costChanges(item.variant_id, ctx.businessId, unit[i]!, options.knownCosts.get(item.variant_id), ctx.now));
    });
  }
  return changes;
}

// ---------------------------------------------------------------------------
// Toma de inventario
// ---------------------------------------------------------------------------

export interface CountEntry {
  variantId: string;
  /** Lo que dice el sistema al momento de contar. */
  systemMilli: number;
  countedMilli: number;
  /** false: el producto no tiene control de stock; contarlo (aunque coincida) lo empieza. */
  tracked?: boolean;
}

/** Un movimiento por cada producto con diferencia (lo que coincide no genera nada). */
export function buildCountChanges(entries: CountEntry[], ctx: StockContext): { countId: string; changes: LocalChange[] } {
  const countId = ctx.newId();
  const changes = entries
    .filter((e) => e.countedMilli !== e.systemMilli || e.tracked === false)
    .map((e) =>
      buildMovement(
        { variantId: e.variantId, type: 'count', qtyMilli: e.countedMilli - e.systemMilli, refType: 'count', refId: countId, reason: 'Toma de inventario' },
        ctx,
      ),
    );
  return { countId, changes };
}
